import React, { useState, useEffect, useCallback } from "react";
import { observer } from "mobx-react";
import { conditionsOperatorObject } from "Static/constant";
import DropdownButton from "./DropdownButton";
import ActionButton from "./ActionButton";
import InputTextBox from "Components/common/InputTextBox";
import SingleSelectDropdown from "Pages/Dashboard/settings/Calendar/Pages/Events/CreateEvent/Components/SingleSelectDropdown";
import SingleDropdown from "./SingleDropdown";
import ContactAutoCompleteDropdown from "Components/PreviewPopup/ContactAutoCompleteDropdown";
import {
  employeeCountDropdown,
  revenueRange
} from "Pages/Dashboard/Accounts/AccountConstants";
import { settingsStoreData } from "Stores/settingsData";
import userDetail from "Stores/userDetail";
import Utils from "Utils/utils";

export const ContactFieldFilter = observer(props => {
  const {
    label = "",
    keyValue = "",
    activeFilter = "",
    data = [],
    errMsg = "",
    setErrMsg = () => {},
    setActiveFilter = () => {},
    setAction = () => {},
    selectedFilterCbk = () => {},
    hasDropdown = true,
    disableAddButton = false,
    customConditionalFieldList = [],
    // When true: render the operator on the SAME row as the field name (with its
    // full "Equals" label) and the value on its own full-width row below. Opt-in
    // so the Contacts-page filter keeps its original two-row layout.
    inlineOperator = false
  } = props || {};

  const { customFieldsCount = 0 } = userDetail?.userFeatureFlag || {};

  let toggleDropdown = hasDropdown ? activeFilter === keyValue : true;

  const [activeDropdownIndex, setActiveDropdownIndex] = useState(false);
  const [fieldNameObject, setFieldNameObject] = useState({});
  const [disabledContactFields, setDisabledContactFields] = useState([]);
  // Per-row in-progress text value. In the auto-apply (sequence) panel we hold
  // the typed value locally and only push it to the store on commit (Enter /
  // blur / the → button) so each keystroke doesn't trigger a re-query.
  const [valueDrafts, setValueDrafts] = useState({});
  // Chip panel: the builder is hidden behind "+ Add condition" until requested.
  const [showBuilder, setShowBuilder] = useState(false);
  let customFieldData = [];

  const clearDraft = index => {
    setValueDrafts(prev => {
      if (!(index in prev)) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  // Commit the typed draft (or keep the existing value) to the store, which is
  // what fires the debounced auto-apply.
  const commitValueDraft = (index = 0) => {
    const draft = valueDrafts?.[index];
    if (draft === undefined) return;
    errMsg && setErrMsg();
    const tempData = [...data];
    tempData[index]["value"] = draft;
    setAction(keyValue, tempData);
    clearDraft(index);
  };

  const validateFieldNameAlreadyExist = (newFieldName = "") => {
    let count = 0;
    if (data?.length > 1) {
      data.some(element => {
        if (element?.fieldName === newFieldName) {
          count++;
        }
      });
    }
    return !count > 0;
  };

  const disableConditionFields = data => {
    let arr = [];
    if (data?.length) {
      data.map(item => {
        arr.push(item?.fieldName);
        return arr;
      });
    }
    setDisabledContactFields(arr.filter(Boolean));
  };

  const handleAction = (refId = "", item = "", index = "") => {
    if (refId === "fieldName") {
      if (!validateFieldNameAlreadyExist(item?.key)) {
        return;
      }
      selectedFilterCbk("add", keyValue);
    }
    errMsg && setErrMsg();
    const { key = "", subKey = "", value = "" } = item || {};
    let tempData = [...data];
    const keyLower = item?.key?.toLowerCase();

    // Handle IS_SET and IS_NOT_SET operators - they don't need values
    if (refId === "operator" && ["IS_SET", "IS_NOT_SET"].includes(subKey)) {
      tempData[index]["value"] = "";
    } else {
      tempData[index]["value"] = ["employeecount", "revenue"].includes(keyLower)
        ? keyLower === "employeecount"
          ? "0 - 25"
          : "$0 to $1M"
        : "";
    }

    tempData[index][refId] = refId === "fieldName" ? key : subKey || "";
    tempData[index]["label"] = value || "";
    setActiveDropdownIndex(false);
    setAction(keyValue, tempData);
    disableConditionFields(tempData);
    clearDraft(index);
  };

  // Direct store write — used by selection-style values (range dropdowns, email
  // autocomplete) which commit immediately, and by the Contacts page text input.
  const handleInputAction = (id = "", value = "", index = 0) => {
    errMsg && setErrMsg();
    let tempData = [...data];
    tempData[index]["value"] = value || "";
    setAction(keyValue, tempData);
  };

  // Sequence panel text input: keep keystrokes local so auto-apply doesn't fire
  // until the value is committed.
  const handleDraftChange = (id = "", value = "", index = 0) => {
    errMsg && setErrMsg();
    setValueDrafts(prev => ({ ...prev, [index]: value }));
  };

  const operatorList = [
    {
      value: "<span class='conditionSymbol'>&equals;</span><span class='conditionLabel'>Equals</span>",
      subKey: "EQUALS",
      key: "equalCond",
      method: handleAction
    },
    {
      value: "<span class='conditionSymbol'>&#8717;</span><span class='conditionLabel'>Contains</span>",
      subKey: "CONTAINS",
      key: "containCond",
      method: handleAction
    },
    {
      value: "<span class='conditionSymbol'>&ne;</span><span class='conditionLabel'>Not equals</span>",
      subKey: "NOT_EQUALS",
      key: "notEqualCond",
      method: handleAction
    },
    {
      value:
        "<span class='conditionSymbol customSymbol' style='font-Size:17px;'>&lcub;&rcub;</span><span class='conditionLabel'>Is set</span>",
      subKey: "IS_SET",
      key: "isSet",
      method: handleAction
    },
    {
      value:
        "<span class='conditionSymbol customSymbol strikeThrough' style='font-Size:17px;'>&lcub;&rcub;</span><span class='conditionLabel'>Is not set</span>",
      subKey: "IS_NOT_SET",
      key: "isNotSet",
      method: handleAction
    }
  ];

  const getCustomContactFields = () => {
    Array.from({ length: customFieldsCount }, (value, index) =>
      customFieldData.push({
        value: `Custom Field ${index + 1}`,
        subKey: `CUSTOM_FIELD_${index + 1}`,
        key: `customField${index + 1}`,
        method: handleAction
      })
    );
    return customFieldData;
  };

  const constructCustomFieldsWithValue = () => {
    const customContactFields = getCustomContactFields();
    return customContactFields.map(item => {
      item.value = settingsStoreData?.customFields?.[item?.key] || item?.value;
      item.hasCustomFieldValue = settingsStoreData?.customFields?.[item?.key]
        ? true
        : false;
      item.disabled = disabledContactFields?.includes(item?.key);
      return item;
    });
  };

  const conditionFieldnameList = [
    {
      value: "First Name",
      subKey: "FIRST_NAME",
      key: "firstName",
      method: handleAction,
      disabled: disabledContactFields?.includes("firstName")
    },
    {
      value: "Last Name",
      subKey: "LAST_NAME",
      key: "lastName",
      method: handleAction,
      disabled: disabledContactFields?.includes("lastName")
    },
    {
      value: "Email Address",
      subKey: "EMAIL_ADDRESS",
      key: "email",
      method: handleAction,
      disabled: disabledContactFields?.includes("email")
    },
    {
      value: "Job Title",
      subKey: "JOB_TITLE",
      key: "jobTitle",
      method: handleAction,
      disabled: disabledContactFields?.includes("jobTitle")
    },
    {
      value: "Phone Number",
      subKey: "PHONE_NUMBER",
      key: "phone",
      method: handleAction,
      disabled: disabledContactFields?.includes("phone")
    },
    {
      value: "Mobile Number",
      subKey: "MOBILE_NUMBER",
      key: "mobilePhone",
      method: handleAction,
      disabled: disabledContactFields?.includes("mobilePhone")
    },
    {
      value: "Other Number",
      subKey: "OTHER_NUMBER",
      key: "otherPhone",
      method: handleAction,
      disabled: disabledContactFields?.includes("otherPhone")
    },
    {
      value: "Company",
      subKey: "COMPANY",
      key: "company",
      method: handleAction,
      disabled: disabledContactFields?.includes("company")
    },
    {
      value: "Industry",
      subKey: "INDUSTRY",
      key: "industry",
      method: handleAction,
      disabled: disabledContactFields?.includes("industry")
    },
    {
      value: "Street",
      subKey: "STREET",
      key: "street",
      method: handleAction,
      disabled: disabledContactFields?.includes("street")
    },
    {
      value: "City",
      subKey: "CITY",
      key: "city",
      method: handleAction,
      disabled: disabledContactFields?.includes("city")
    },
    {
      value: "State",
      subKey: "STATE",
      key: "state",
      method: handleAction,
      disabled: disabledContactFields?.includes("state")
    },
    {
      value: "Country",
      subKey: "COUNTRY",
      key: "country",
      method: handleAction,
      disabled: disabledContactFields?.includes("country")
    },
    {
      value: "Zip Code",
      subKey: "ZIP_CODE",
      key: "zipCode",
      method: handleAction,
      disabled: disabledContactFields?.includes("zipCode")
    },
    {
      value: "Website",
      subKey: "WEBSITE",
      key: "website",
      method: handleAction,
      disabled: disabledContactFields?.includes("website")
    },
    {
      value: "LinkedIn URL",
      subKey: "LINKEDIN_URL",
      key: "linkedinId",
      method: handleAction,
      disabled: disabledContactFields?.includes("linkedinId")
    },
    {
      value: "Facebook",
      subKey: "FACEBOOK",
      key: "facebook",
      method: handleAction,
      disabled: disabledContactFields?.includes("facebook")
    },
    {
      value: "Twitter",
      subKey: "TWITTER",
      key: "twitter",
      method: handleAction,
      disabled: disabledContactFields?.includes("twitter")
    },
    ...constructCustomFieldsWithValue()
  ];

  const getConditionFieldsObject = () => {
    let tempObj = {};
    let tempConditionFieldArr = customConditionalFieldList?.length
      ? customConditionalFieldList
      : conditionFieldnameList;
    tempConditionFieldArr?.length > 0 &&
      tempConditionFieldArr.map(item => {
        tempObj[item?.key] = item?.value;
      });
    setFieldNameObject({ ...fieldNameObject, ...tempObj });
  };

  const getCustomConditonalFields = () => {
    return [...customConditionalFieldList].map(item => ({
      ...item,
      disabled: disabledContactFields?.includes(item?.key),
      method: handleAction
    }));
  };

  const resetOrAddNewItem = (isPush = false) => {
    let newObj = {
      fieldName: "",
      operator: "EQUALS",
      value: ""
    };
    if (isPush) {
      return newObj;
    }
    selectedFilterCbk("remove", keyValue);
    setAction(keyValue, [newObj]);
    disableConditionFields([newObj]);
  };

  const removeItem = index => {
    // Row indices shift on splice — drop transient drafts to avoid desync.
    setValueDrafts({});
    let tempData = [...data];
    let len = tempData?.length;
    if (len) {
      if (len === 1) {
        resetOrAddNewItem();
      } else {
        tempData.splice(index, 1);
        tempData.filter(item => item?.fieldName)?.length < 1 &&
          selectedFilterCbk("remove", keyValue);
        setAction(keyValue, tempData);
        disableConditionFields(tempData);
      }
      errMsg && setErrMsg();
    }
  };

  const addItem = () => {
    let tempData = [...data];
    tempData.push(resetOrAddNewItem(true));
    errMsg && setErrMsg();
    setAction(keyValue, tempData);
  };

  const resetItem = () => {
    errMsg && setErrMsg();
    data?.filter(item => item?.fieldName)?.length && resetOrAddNewItem();
  };

  const employeeCountObj = {
    name: "employeeCount",
    id: "employeeCount",
    type: "select",
    options: [...employeeCountDropdown],
    text: "No Of Employees",
    showLabel: false
  };

  const revenueObj = {
    name: "revenue",
    id: "revenue",
    type: "select",
    options: [...revenueRange],
    text: "Revenue",
    showLabel: false
  };
  const filterFn = Utils.getFilterFn("CONTACT_FIELD_FILTER");

  useEffect(() => {
    if (data?.length === 1 && !data?.fieldName & !data?.value) {
      disableConditionFields(data);
    }
  }, [data]);

  useEffect(() => {
    getConditionFieldsObject();
  }, [settingsStoreData?.customFields]);

  // ──────────────── Chip model (sequence / auto-apply panel) ────────────────
  // Committed conditions render as removable chips; a single committed:false row
  // is the active builder. Typing stays in valueDrafts so it doesn't auto-apply
  // until the user confirms (Enter / "Apply"), at which point the row is
  // committed and a fresh builder is appended.
  const isNoValueOpFn = op => ["IS_SET", "IS_NOT_SET"].includes(op);
  const isRangeFn = fn => ["employeecount", "revenue"].includes((fn || "").toLowerCase());
  const OP_SYMBOL = {
    EQUALS: "=",
    CONTAINS: "contains",
    NOT_EQUALS: "≠",
    IS_SET: "is set",
    IS_NOT_SET: "is not set"
  };

  // One-time normalize: mark complete rows as committed chips + ensure one builder.
  useEffect(() => {
    if (!inlineOperator) return;
    const rows = data || [];
    const committedRows = rows
      .filter(
        r =>
          r?.fieldName &&
          r?.operator &&
          (isNoValueOpFn(r.operator) || (r.value !== "" && r.value != null))
      )
      .map(r => ({ ...r, committed: true }));
    const next = [
      ...committedRows,
      { fieldName: "", operator: "EQUALS", value: "", committed: false }
    ];
    if (JSON.stringify(next) !== JSON.stringify(rows)) setAction(keyValue, next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chipRows = inlineOperator ? (data || []).filter(r => r?.committed === true) : [];
  const builderIndex = inlineOperator
    ? (data || []).findIndex(r => r?.committed !== true)
    : -1;

  const chipLabel = item => {
    const f = fieldNameObject?.[item?.fieldName] || item?.fieldName || "";
    const op = OP_SYMBOL[item?.operator] || "";
    if (isNoValueOpFn(item?.operator)) return `${f} ${op}`;
    return `${f} ${op} ${item?.value || ""}`;
  };

  const removeChip = dataIdx => {
    const rows = [...(data || [])];
    rows.splice(dataIdx, 1);
    if (!rows.some(r => r?.committed === true)) selectedFilterCbk("remove", keyValue);
    setAction(keyValue, rows);
    setValueDrafts({});
    errMsg && setErrMsg();
  };

  // Local value set for the builder row (text + range) without touching the store.
  const setBuilderDraft = (id = "", value = "", index = 0) =>
    setValueDrafts(prev => ({ ...prev, [index]: value }));

  const builderReady = index => {
    const row = (data || [])[index] || {};
    if (!row.fieldName || !row.operator) return false;
    if (isNoValueOpFn(row.operator)) return true;
    const v = valueDrafts?.[index] !== undefined ? valueDrafts[index] : row.value;
    return v !== "" && v != null;
  };

  const commitBuilder = (index = 0) => {
    const rows = [...(data || [])];
    const row = rows[index] || {};
    if (!row.fieldName || !row.operator) return;
    const noVal = isNoValueOpFn(row.operator);
    const draftVal = valueDrafts?.[index];
    const value = noVal ? "" : draftVal !== undefined ? draftVal : row.value || "";
    if (!noVal && (value === "" || value == null)) return;
    rows[index] = { ...row, value, committed: true };
    rows.push({ fieldName: "", operator: "EQUALS", value: "", committed: false });
    selectedFilterCbk("add", keyValue);
    setAction(keyValue, rows);
    clearDraft(index);
    setShowBuilder(false); // collapse back to "+ Add condition" after committing
    errMsg && setErrMsg();
  };

  return (
    <div className="conditionField">
      {hasDropdown && (
        <DropdownButton
          label={label}
          count={data?.filter(item => item?.fieldName)?.length || 0}
          keyValue={keyValue}
          toggleDropdown={toggleDropdown}
          callback={setActiveFilter}
        />
      )}
      {toggleDropdown && (
        <div className="dropdownSearchBox">
          <span className="horizontalBorderLine" />
          {inlineOperator && (
            <div className="filterChipWrap">
              {chipRows.length > 0 && (
                <div className="filterChips">
                  {chipRows.map((c, i) => (
                    <span className="filterChip" key={`chip-${i}`}>
                      <span className="chipText">{chipLabel(c)}</span>
                      <i
                        className="material-icons chipRemove"
                        onClick={() => removeChip(data.indexOf(c))}
                      >
                        close
                      </i>
                    </span>
                  ))}
                </div>
              )}
              {!showBuilder && (
                <div
                  className="addNewButton chipAddBtn"
                  onClick={() => {
                    setShowBuilder(true);
                    setActiveDropdownIndex(`fieldName-${builderIndex}`);
                  }}
                >
                  <i className="material-icons addIcon">add_circle_outline</i>
                  <span className="text">Add condition</span>
                </div>
              )}
              {showBuilder &&
                builderIndex > -1 &&
                (() => {
                  const index = builderIndex;
                  const bItem = data[index] || {};
                  const bFieldName = bItem.fieldName || "";
                  const bOperator = bItem.operator || "EQUALS";
                  const bIsRange = isRangeFn(bFieldName);
                  const bIsNoVal = isNoValueOpFn(bOperator);
                  const bRangeProps =
                    bFieldName?.toLowerCase() === "employeecount"
                      ? employeeCountObj
                      : revenueObj;
                  const bDraft =
                    valueDrafts?.[index] !== undefined ? valueDrafts[index] : "";
                  return (
                    <div className="chipBuilder">
                      <div className="fieldnameDropdown fieldnameOperatorRow">
                        <SingleDropdown
                          isDropdownAllowed={true}
                          options={
                            customConditionalFieldList?.length
                              ? getCustomConditonalFields()
                              : conditionFieldnameList
                          }
                          defaultValue={fieldNameObject?.[bFieldName] || ""}
                          label="Select field"
                          referenceId="fieldName"
                          parentIndex={index}
                          hasCustomFieldValue={true}
                          showDropdown={activeDropdownIndex === `fieldName-${index}`}
                          setActiveDropdownIndex={setActiveDropdownIndex}
                          search={true}
                          filterFn={filterFn}
                        />
                        {bFieldName && !bIsRange && (
                          <SingleDropdown
                            isDropdownAllowed={true}
                            options={operatorList}
                            defaultValue={conditionsOperatorObject?.[bOperator] || ""}
                            showDropdown={activeDropdownIndex === `operator-${index}`}
                            setActiveDropdownIndex={setActiveDropdownIndex}
                            parentIndex={index}
                            referenceId="operator"
                            className="inlineOperatorLabel"
                          />
                        )}
                      </div>
                      {bFieldName && !bIsNoVal && (
                        <div className="fieldOperatorValue valueOnlyRow">
                          {!bIsRange ? (
                            <div className="valueWithApply">
                              <InputTextBox
                                name="text"
                                type="text"
                                value={bDraft}
                                change={setBuilderDraft}
                                cbk={e => {
                                  if (e?.key === "Enter" || e?.keyCode === 13)
                                    commitBuilder(index);
                                }}
                                parentIndex={index}
                                placeHolder="Value"
                                autoFocus={true}
                              />
                              {builderReady(index) && (
                                <span
                                  className="applyValueLink"
                                  onClick={() => commitBuilder(index)}
                                >
                                  Apply
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="valueWithApply">
                              <SingleSelectDropdown
                                {...bRangeProps}
                                change={(p1, p2) => setBuilderDraft(p1, p2, index)}
                                value={bDraft || bRangeProps?.options?.[0] || ""}
                                key={bFieldName}
                              />
                              <span
                                className="applyValueLink"
                                onClick={() => commitBuilder(index)}
                              >
                                Apply
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      {bFieldName && bIsNoVal && (
                        <div className="fieldOperatorValue valueOnlyRow chipNoValueRow">
                          <span
                            className="applyValueLink"
                            onClick={() => commitBuilder(index)}
                          >
                            Apply
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}
            </div>
          )}
          {!inlineOperator &&
            ((data?.length && (
            <div className="conditionalFieldCard">
              {data.map((item, index) => {
                const {
                  fieldName = "",
                  operator = "EQUALS",
                  value = ""
                } = item || {};
                let dropdownProps =
                  fieldName?.toLowerCase() === "employeecount"
                    ? employeeCountObj
                    : revenueObj;
                // employeecount/revenue use a fixed select (no operator); IS_SET/
                // IS_NOT_SET take no value.
                const isRangeField = ["employeecount", "revenue"].includes(
                  fieldName?.toLowerCase()
                );
                const isNoValueOp = ["IS_SET", "IS_NOT_SET"].includes(operator);
                const operatorDropdown = (
                  <SingleDropdown
                    isDropdownAllowed={true}
                    options={operatorList}
                    defaultValue={conditionsOperatorObject?.[operator] || ""}
                    showDropdown={
                      !isRangeField &&
                      activeDropdownIndex === `operator-${index}`
                    }
                    setActiveDropdownIndex={setActiveDropdownIndex}
                    parentIndex={index}
                    referenceId="operator"
                    className={inlineOperator ? "inlineOperatorLabel" : ""}
                  />
                );
                return (
                  <div
                    className="conditionalFieldWrap"
                    key={`conditionalField${index}`}
                  >
                    <span className="horizontalBorderLine" />
                    <div className="conditionalField">
                      <div
                        className={`fieldnameDropdown ${
                          inlineOperator ? "fieldnameOperatorRow" : ""
                        }`}
                      >
                        <SingleDropdown
                          isDropdownAllowed={true}
                          options={
                            customConditionalFieldList?.length
                              ? getCustomConditonalFields()
                              : conditionFieldnameList
                          }
                          defaultValue={fieldNameObject?.[fieldName] || ""}
                          label="Select field"
                          referenceId="fieldName"
                          parentIndex={index}
                          hasCustomFieldValue={true}
                          showDropdown={
                            activeDropdownIndex === `fieldName-${index}`
                          }
                          setActiveDropdownIndex={setActiveDropdownIndex}
                          search={true}
                          filterFn={filterFn}
                        />
                        {inlineOperator &&
                          fieldName &&
                          !isRangeField &&
                          operatorDropdown}
                        <i
                          className="material-icons-outlined deleteIcon"
                          onClick={() => removeItem(index)}
                        >
                          delete
                        </i>
                      </div>
                      {fieldName && !(inlineOperator && isNoValueOp) && (
                        <div
                          className={`fieldOperatorValue ${
                            inlineOperator ? "valueOnlyRow" : ""
                          } ${!fieldName ? "disableInput" : ""}`}
                        >
                          {!inlineOperator && operatorDropdown}
                          {!isRangeField && !isNoValueOp ? (
                            <>
                              {inlineOperator ? (
                                <div className="valueWithApply">
                                  <InputTextBox
                                    name="text"
                                    type="text"
                                    value={
                                      valueDrafts?.[index] !== undefined
                                        ? valueDrafts[index]
                                        : value
                                    }
                                    change={handleDraftChange}
                                    cbk={e => {
                                      if (e?.key === "Enter" || e?.keyCode === 13)
                                        commitValueDraft(index);
                                    }}
                                    onBlur={() => commitValueDraft(index)}
                                    parentIndex={index}
                                    placeHolder="Value"
                                    autoFocus={true}
                                  />
                                  {valueDrafts?.[index] !== undefined && (
                                    <span
                                      className="applyValueLink"
                                      onClick={() => commitValueDraft(index)}
                                    >
                                      Apply
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <InputTextBox
                                  name="text"
                                  type="text"
                                  value={value}
                                  change={handleInputAction}
                                  parentIndex={index}
                                  placeHolder="Value"
                                />
                              )}
                              {["email", "email_address"]?.includes(
                                fieldName?.toLowerCase()
                              ) && (
                                <ContactAutoCompleteDropdown
                                  inputText={
                                    valueDrafts?.[index] !== undefined
                                      ? valueDrafts[index]
                                      : value
                                  }
                                  selectedCallbk={(contact = {}) => {
                                    handleInputAction(
                                      keyValue,
                                      contact?.email,
                                      index
                                    );
                                    clearDraft(index);
                                  }}
                                />
                              )}
                            </>
                          ) : !isNoValueOp ? (
                            <SingleSelectDropdown
                              {...dropdownProps}
                              change={(param1, param2) => {
                                handleInputAction(param1, param2, index);
                              }}
                              value={value || dropdownProps?.options?.[0] || ""}
                              key={fieldName}
                            />
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )) ||
              "")}
          {!inlineOperator && (
            <ActionButton
              resetCbk={resetItem}
              addCbk={addItem}
              isReset={false}
              disableAddButton={disableAddButton}
              addLabel="Add"
            />
          )}
          {!inlineOperator && errMsg === keyValue && (
            <span className="errMessage">
              {data?.some(item =>
                ["IS_SET", "IS_NOT_SET"].includes(item?.operator)
              )
                ? "Please select a field"
                : "Please enter a value"}
            </span>
          )}
        </div>
      )}
    </div>
  );
});

export default ContactFieldFilter;
